/**
 * Remember received-invoices list search for the session. When the user opens
 * /received_invoices with an empty URL, restore the last search from
 * sessionStorage. Any query string in the URL (including id-only) wins and
 * does not read session. Drawer `id` is never persisted.
 */

export const RECEIVED_INVOICES_LAST_SEARCH_KEY =
  'received_invoices:last_search';

export const DRAWER_ID_PARAM = 'id';

export type LastListSearch = {
  search: string;
};

const storage = (): Storage | undefined => {
  try {
    return globalThis.sessionStorage;
  } catch {
    return undefined;
  }
};

export const withoutDrawerId = (search: string): string => {
  const params = new URLSearchParams(search);
  params.delete(DRAWER_ID_PARAM);
  const str = params.toString();
  return str ? `?${str}` : '';
};

export const hasExplicitSearch = (search: string): boolean =>
  withoutDrawerId(search) !== '';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isLastListSearch = (value: unknown): value is LastListSearch =>
  isRecord(value) && typeof value['search'] === 'string';

export const saveLastListSearch = (search: string): void => {
  try {
    const normalized = withoutDrawerId(search);
    const payload: LastListSearch = { search: normalized };
    storage()?.setItem(
      RECEIVED_INVOICES_LAST_SEARCH_KEY,
      JSON.stringify(payload)
    );
  } catch {
    // sessionStorage unavailable (e.g. private/sandboxed mode) — skip persistence
  }
};

export const readLastListSearch = (): LastListSearch | undefined => {
  try {
    const raw = storage()?.getItem(RECEIVED_INVOICES_LAST_SEARCH_KEY);
    if (!raw) return undefined;
    const parsed: unknown = JSON.parse(raw);
    return isLastListSearch(parsed) ? parsed : undefined;
  } catch {
    return undefined;
  }
};

export const isEmptyListUrl = (locationSearch: string): boolean =>
  locationSearch === '';

/**
 * List search for form/API: **URL first**, session second.
 * - Any URL query (filters, page, id-only, …) → use `locationSearch` as-is.
 * - Blank URL → last saved search from session, or `''`.
 */
export const resolveListSearch = (locationSearch: string): string => {
  if (!isEmptyListUrl(locationSearch)) return locationSearch;
  return readLastListSearch()?.search ?? '';
};
